import { Box, Paper, Stack } from '@mui/material';
import React from 'react';
import AIChatMessage from '../../models/AIChatMessage';
import { MarkdownRenderer } from '../MarkdownRenderer';
import { Palette } from 'Palette';

export interface FundOpportunityChatMessagesProps {
  messages: AIChatMessage[];
  isProcessing: boolean;
  lastMessageSender: AIChatMessage['role'] | null;
  chatContainerRef: React.RefObject<HTMLDivElement | null>;
}

const FundOpportunityChatMessages: React.FC<FundOpportunityChatMessagesProps> = ({
  messages,
  isProcessing,
  lastMessageSender,
  chatContainerRef,
}) => (
  <Box
    ref={chatContainerRef}
    sx={{
      flex: 1,
      borderRadius: '24px',
      backgroundColor: 'transparent',
      px: { xs: 1, md: 2 },
      py: { xs: 1, md: 2 },
      minHeight: { xs: '260px', md: '360px' },
      overflowY: 'auto',
    }}
  >
    {messages.map((msg, index) => {
      const key =
        msg.timestamp instanceof Date
          ? `${msg.timestamp.getTime()}-${index}`
          : `${index}-${Math.random()}`;

      const isUser = msg.role === 'user';

      return (
        <Stack
          data-message-index={index}
          key={key}
          alignItems={isUser ? 'flex-end' : 'flex-start'}
          spacing={0.5}
          sx={{ mb: 2.5 }}
        >
          <Paper
            elevation={0}
            sx={{
              maxWidth: '90%',
              px: 3,
              py: '0.5rem',
              borderRadius: '50px',
              backgroundColor: isUser ? '#E8EDE9' : '#F8F9FA',
              color: isUser ? '#34495E' : '#333333',
              whiteSpace: 'pre-wrap',
              fontSize: '0.95rem',
              border: 'none',
              textAlign: 'left',
              '& > p': {
                margin: 0,
              },
            }}
          >
            <MarkdownRenderer>{msg.parts.map((part) => part.text).join('\n\n')}</MarkdownRenderer>
          </Paper>
        </Stack>
      );
    })}

    {isProcessing && lastMessageSender === 'user' && (
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
        <Paper
          elevation={0}
          sx={{
            px: 2,
            py: 1.25,
            borderRadius: '18px 18px 18px 6px',
            backgroundColor: Palette.tone2.background,
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
          }}
        >
          {[0, 1, 2].map((dot) => (
            <Box
              key={dot}
              sx={{
                width: '8px',
                height: '8px',
                borderRadius: '24px',
                backgroundColor: 'rgba(20, 60, 52, 0.6)',
                animation: 'blink 1.4s infinite',
                animationDelay: `${dot * 0.2}s`,
                '@keyframes blink': {
                  '0%, 80%, 100%': { opacity: 0 },
                  '40%': { opacity: 1 },
                },
              }}
            />
          ))}
        </Paper>
      </Stack>
    )}
  </Box>
);

export default FundOpportunityChatMessages;
